import { useSnackbar } from "notistack";
import React, { useContext } from "react";
import { useTranslation } from "react-i18next";
import { GetAssetsForDbidContentModel, IndexModel } from "../../../../api/APIClient";

import Circle from "../../../../../assets/spriteIcons/circle.svg";
import Co2 from "../../../../../assets/spriteIcons/co2.svg";
import Sensor from "../../../../../assets/spriteIcons/sensor.svg";
import Thermometer from "../../../../../assets/spriteIcons/thermometer.svg";
import {
    fetchAlarmPanel,
    fetchAssetInformationByDbId,
    fetchAssetInformationById,
    fetchAssetsForDbid,
    fetchDocumentsByTagApi,
    fetchIsoPanel,
    fetchPropertyPanel,
    fetchSignalPanel,
    fetchThreadsPanel,
} from "../../../../api/Fetchers";
import {
    DocumentsRightPanelModel,
    FetchAssetInformationByDbIdQuery,
    FetchAssetInformationByIdQuery,
    FullAssetInformation,
    RenderModel,
    RenderModelType,
    RightPanelsModel,
} from "../../../../Models/AssetInformationModels";
import { ChartsModel } from "../../../../Models/SignalModels";
import PanelProjectContext from "../../../PanelProjectProvider";
import { ProjectContext, ProjectModelContext } from "../../Context/ProjectContext";
import {
    colorHexToRgb,
    convertHexToRGB,
    ViewerMode,
} from "../ForgeViewer/BIMDigitalTwinViewer/Utils";
import { RenderConfigInput } from "../../Settings/Subscriptions/Models";

interface IObjTest {
    xcoord: number;
    ycoord: number;
    data: ChartsModel;
}
interface Selection {
    dbIdArray: number[];
    fragIdsArray: number[];
    // eslint-disable-next-line  @typescript-eslint/no-explicit-any
    model: any;
    nodeArray: number[];
}

interface AggregateSelectionEvent {
    mouseButton: number | undefined;
    selections: Selection[];
}
interface IPanelProjectContext {
    setDbidsToRender: React.Dispatch<React.SetStateAction<number[]>>;
    setHoveredDeviceInfo: React.Dispatch<React.SetStateAction<IObjTest | undefined>>;
    setRightPanelsData: React.Dispatch<React.SetStateAction<RightPanelsModel | null>>;
    setIsRightPanelOpen: React.Dispatch<React.SetStateAction<boolean>>;
    setIsViewerLoading: React.Dispatch<React.SetStateAction<boolean>>;
    setIsRenderingItems: React.Dispatch<React.SetStateAction<boolean>>;
    setAssetSelection: React.Dispatch<React.SetStateAction<IndexModel | null>>;
    viewerMode: number;
    setViewerMode: React.Dispatch<React.SetStateAction<ViewerMode>>;
    setAssetsWithSameDbid: React.Dispatch<
        React.SetStateAction<GetAssetsForDbidContentModel[] | undefined>
    >;
    setDbIdsToEdit: React.Dispatch<React.SetStateAction<number[] | undefined>>;
    setRenderModel: React.Dispatch<React.SetStateAction<RenderModel[] | null>>;
    userId: string;
    setView: React.Dispatch<React.SetStateAction<string>>;
    setRenderModelTypes: React.Dispatch<React.SetStateAction<RenderModelType[]>>;
    setRightPanelButtonsLoading: React.Dispatch<React.SetStateAction<boolean>>;
    smallVolumesConfig: RenderConfigInput;
    setSmallVolumesConfig: (RenderConfigInput) => void;
    smallVolumesSwitch: boolean;
}

const useAsset = () => {
    const project = useContext(ProjectContext);
    const model = useContext(ProjectModelContext);
    const { enqueueSnackbar } = useSnackbar();
    const {
        setHoveredDeviceInfo,
        setRightPanelsData,
        setIsRightPanelOpen,
        setIsViewerLoading,
        setIsRenderingItems,
        setAssetSelection,
        viewerMode,
        setViewerMode,
        setDbidsToRender,
        setRenderModel,
        setAssetsWithSameDbid,
        setDbIdsToEdit,
        setView,
        userId,
        smallVolumesConfig,
        smallVolumesSwitch,
        setRenderModelTypes,
        setRightPanelButtonsLoading,
    } = useContext<IPanelProjectContext>(PanelProjectContext);

    const { t } = useTranslation("project");
    const hexToRgbArray = (hex: string): number[] => {
        const hexValue = hex.replace(/^#/, "");
        const bigint = parseInt(hexValue, 16);
        // eslint-disable-next-line no-bitwise
        return [(bigint >> 16) & 255, (bigint >> 8) & 255, bigint & 255];
    };
    const createSphereOverlayAsync = (
        tag: string,
        defaultRadio: number,
        color: string,
        opacity: number,
        dbIds?: number[] | undefined | null
    ) => {
        if (dbIds) {
            // Añade los fragmentos a la selección.Necesaria para eviara errores
            window.NOP_VIEWER.addFrags(dbIds);
            // Se obtiene la caja delimitadora modificada del mundo para los objetos con los IDs proporcionados.
            const bBox = window.NOP_VIEWER.getModifiedWorldBoundingBox(dbIds);

            // Calcula las diferencias en las coordenadas X, Y y Z.
            const diffx = bBox.max.x - bBox.min.x;
            const diffy = bBox.max.y - bBox.min.y;
            const diffz = bBox.max.z - bBox.min.z;

            // Encuentra la diferencia máxima entre las tres dimensiones.
            const maxDiff = Math.max(diffx, diffy, diffz);

            if (maxDiff < smallVolumesConfig?.input?.minSize && smallVolumesSwitch) {
                requestAnimationFrame(() => {
                    // Se obtiene el centro de la caja delimitadora.
                    const center = bBox.getCenter();

                    // Se calcula el radio de la esfera basada en la distancia entre los extremos de la caja.
                    const radius = bBox.max.distanceTo(bBox.min) / 2 + defaultRadio;

                    // Se crea una geometría de esfera con el radio calculado y una segmentación simplificada.
                    const geometry = new THREE.SphereGeometry(radius, 120, 60);

                    // Se crea el material de la esfera utilizando el color y la opacidad dados.
                    const material = new THREE.MeshBasicMaterial({ color, opacity });

                    // Se crea el mesh de la esfera con la geometría y el material.
                    const sphereMesh = new THREE.Mesh(geometry, material);

                    // Se establece la posición de la esfera en el centro de la caja delimitadora.
                    sphereMesh.position.set(center.x, center.y, center.z);

                    // Si ya existe una escena de superposición con la etiqueta dada, elimina la superposición anterior.
                    if (window.NOP_VIEWER.impl.overlayScenes[tag]) {
                        window.NOP_VIEWER.impl.removeOverlay(tag, sphereMesh);
                    } else {
                        // Si no existe, crea una nueva escena de superposición.
                        window.NOP_VIEWER.impl.createOverlayScene(tag);
                    }

                    // Añade la esfera a la escena de superposición.
                    window.NOP_VIEWER.impl.addOverlay(tag, sphereMesh);

                    // Convierte el color hexadecimal a un array RGB.
                    const rgbColor = hexToRgbArray(color);

                    // Ajusta el color especular de los elementos resaltados.
                    window.NOP_VIEWER.setElementsSpecularColor(rgbColor, dbIds);
                });
            } else {
                // Si el tamaño es mayor o no se cumplen las condiciones, elimina la escena de superposición.
                window.NOP_VIEWER.impl.removeOverlayScene(tag);
            }
        }
    };

    // Convertir hex a RGB

    const colorRenderModel = (renderModelToPaint: RenderModel[]) => {
        return new Promise((resolve) => {
            renderModelToPaint?.forEach((element) => {
                const hexColor = element.style?.hexColor;
                if (!element.dbIds) return;
                // Funcion que crea la esfera de los volumenes pequeños; espera un radio de la esfera por parte de la api, un color hex tambien por parte de la api, la opacidad que en este caso es fija y los dbids a loq ue se ha de aplicar
                createSphereOverlayAsync(
                    `name_${element.dbIds[0]}`,
                    smallVolumesConfig.input.sphereRadius,
                    hexColor!,
                    0.5,
                    element.dbIds
                );

                if (!hexColor || hexColor === "clear") return;
                const rgbColor = colorHexToRgb(hexColor!);
                const covertedColor = [rgbColor.r * 255, rgbColor.g * 255, rgbColor.b * 255];
                // window.NOP_VIEWER.setDbIdsColorByHex(element.dbIds!, hexColor!);
                window.NOP_VIEWER.setElementsSpecularColorByColor(covertedColor, element.dbIds!);
            });
            resolve(true);
        });
    };

    async function PrintDeviceIcons(res: ChartsModel[], alternate: number) {
        window.NOP_VIEWER.getExtension("Autodesk.DataVisualization");
        // eslint-disable-next-line  @typescript-eslint/no-explicit-any
        const devices = res;

        const SensorStyleDefinitions = {
            sensor: {
                url: Sensor,
            },
            co2: {
                url: Co2,
            },
            temperature: {
                url: Thermometer,
            },
            default: {
                url: Circle,
            },
        };

        const dataVizExt = window.NOP_VIEWER.getExtension(
            "Autodesk.DataVisualization"
        ) as Autodesk.Extensions.DataVisualization;
        const DATAVIZEXTN = Autodesk.DataVisualization.Core;
        const viewableType = DATAVIZEXTN.ViewableType.SPRITE;
        const styleMap = {};

        dataVizExt.removeAllViewables();

        // // Create model-to-style map from style definitions.
        const viewableData = new DATAVIZEXTN.ViewableData();

        viewableData.spriteSize = 30;
        if (devices && devices.length > 0) {
            devices.forEach((device) => {
                if (device?.dbIds && device?.dbIds.length > 0) {
                    Object.entries(SensorStyleDefinitions).forEach(([type, styleDef]) => {
                        styleMap[type] = new DATAVIZEXTN.ViewableStyle(
                            viewableType,
                            new THREE.Color(convertHexToRGB(device?.icon?.color)),
                            styleDef.url
                        );
                    });

                    // eslint-disable-next-line
                    const style = styleMap[device?.icon?.type!] || styleMap["default"];
                    // eslint-disable-next-line
                    window.NOP_VIEWER.addFrags(device?.dbIds!);
                    const dbIdBox = window.NOP_VIEWER.getModifiedWorldBoundingBox(
                        // eslint-disable-next-line
                        device?.dbIds!
                    );
                    const dbIdCenter = dbIdBox.center();
                    const viewable = new DATAVIZEXTN.SpriteViewable(
                        dbIdCenter,
                        style,
                        device.dbIds![0] + alternate
                    );
                    viewableData.addViewable(viewable);
                }
            });

            let click = false;

            const onItemHovering = (event) => {
                if (window.NOP_VIEWER.getExtension("Autodesk.DataVisualization")) {
                    if (event.hovering) {
                        const deviceId = event.dbId;
                        const device = devices.find((elm) =>
                            elm?.dbIds?.includes(deviceId - alternate)
                        );
                        if (device) {
                            const deviceBox = window.NOP_VIEWER.getModifiedWorldBoundingBox(
                                device!.dbIds!
                            );
                            const deviceCenter = deviceBox?.center();
                            const mappedPosition =
                                window.NOP_VIEWER.impl.worldToClient(deviceCenter);

                            const objTest: IObjTest = {
                                xcoord: mappedPosition.x,
                                ycoord: mappedPosition.y - 10,
                                data: device,
                            };
                            setHoveredDeviceInfo(objTest);
                        }
                    } else if (click === false) {
                        setHoveredDeviceInfo(undefined);
                    }
                }
            };

            const onItemClick = (event) => {
                if (window.NOP_VIEWER.getExtension("Autodesk.DataVisualization")) {
                    const deviceId = event.dbId;
                    const device = devices.find((elm) =>
                        elm?.dbIds?.includes(deviceId - alternate)
                    );
                    if (device) {
                        const deviceBox = window.NOP_VIEWER.getModifiedWorldBoundingBox(
                            device.dbIds!
                        );
                        const deviceCenter = deviceBox?.center();
                        const mappedPosition = window.NOP_VIEWER.impl.worldToClient(deviceCenter);

                        const objTest: IObjTest = {
                            xcoord: mappedPosition.x,
                            ycoord: mappedPosition.y - 10,
                            data: device,
                        };
                        click = true;
                    }

                    if (deviceId === 0) {
                        setHoveredDeviceInfo(undefined);
                        click = false;
                    }
                }
            };

            await viewableData.finish();
            setIsViewerLoading(false);
            const DataVizCore = Autodesk.DataVisualization.Core;

            window.NOP_VIEWER?.removeEventListener(DataVizCore.MOUSE_CLICK, onItemClick);
            window.NOP_VIEWER?.removeEventListener(DataVizCore.MOUSE_CLICK, onItemHovering);
            window.NOP_VIEWER?.addEventListener(DataVizCore.MOUSE_CLICK, onItemClick);
            window.NOP_VIEWER?.addEventListener(DataVizCore.MOUSE_HOVERING, onItemHovering);

            dataVizExt.addViewables(viewableData);
        }
    }

    const renderHTMLTooltips = (renderInformation: RenderModel[], showTooltips?: boolean) => {
        if (renderInformation.every((d) => d.dbIds !== null && d.dbIds !== undefined)) {
            if (
                renderInformation.every((e) => e.type === RenderModelType.Parent) &&
                viewerMode !== ViewerMode.PrototypeViews
            ) {
                enqueueSnackbar(`${t("viewer.messages.showOnlyParents")}`, {
                    variant: "info",
                    anchorOrigin: {
                        horizontal: "right",
                        vertical: "bottom",
                    },
                });
            }
            setIsRenderingItems(true);
            window.NOP_VIEWER.clearAllLabels();
            renderInformation?.forEach((e) => {
                window.NOP_VIEWER.addLabelHeatMap(
                    e.dbIds!,
                    e.tooltip,
                    e.style?.hexColor,
                    e.tooltip?.data,
                    Boolean(showTooltips),
                    undefined
                );
            });
            if (window.NOP_VIEWER.showLabels) {
                window.NOP_VIEWER.showAllLabels();
            } else {
                window.NOP_VIEWER.hideAllLabels();
            }
        }
    };

    const renderModelViewer = (renderInformation: RenderModel[], showHtmlTooltips: boolean) => {
        setIsRenderingItems(true);
        window.NOP_VIEWER.mode = ViewerMode.AssetInfo;
        setViewerMode(window.NOP_VIEWER.mode);
        window.NOP_VIEWER.clearThemingAndSpecularColor();
        window.NOP_VIEWER.setOpacityModel(1, undefined);

        const dbidsToRender = renderInformation.map((element) => {
            return element.dbIds;
        });

        const fullArrayOfDbidToRender: (number | undefined | null)[] = dbidsToRender.flat();
        // window.NOP_VIEWER.isolate(fullArrayOfDbidToRender as number[]);
        window.NOP_VIEWER.setOpacityModel(
            window.NOP_VIEWER.viewingMode.backgroundOpacity,
            window.NOP_VIEWER.viewingMode.backgroundColor
        );

        if (window.NOP_VIEWER.isolated) {
            window.NOP_VIEWER.isolate(fullArrayOfDbidToRender as number[]);
        }
        window.NOP_VIEWER.fitToView(fullArrayOfDbidToRender as number[]);
        window.NOP_VIEWER.setLightPreset(window.NOP_VIEWER.viewingMode.light);
        window.NOP_VIEWER.setEnvMapBackground(false);

        window.NOP_VIEWER.setElementsSpecularOriginalColor(fullArrayOfDbidToRender as number[]);

        if (window.NOP_VIEWER.viewingMode.colored) {
            colorRenderModel(renderInformation);
        }

        // Html tooltips
        if (showHtmlTooltips) {
            renderHTMLTooltips(renderInformation);
        }
        window.NOP_VIEWER.dbidsToRender = fullArrayOfDbidToRender as number[];
        setDbidsToRender(fullArrayOfDbidToRender as number[]);
    };

    const manageAssetInformation = (assetInfo: FullAssetInformation) => {
        const { render, rightPanels, index } = assetInfo;
        setRenderModel(render ?? null);

        if (index) {
            setAssetSelection(index);
        }
        setIsRenderingItems(false);

        // Render on Model
        const renderInfo: RenderModel[] | undefined | null = render;

        if (
            renderInfo &&
            renderInfo.length > 0 &&
            renderInfo.some((e) => e.dbIds && e.dbIds?.length > 0)
        ) {
            const newRenderModelTypes: RenderModelType[] = [];

            const types = [
                RenderModelType.Children,
                RenderModelType.Parent,
                RenderModelType.Selected,
                RenderModelType.Noozle,
            ];

            types.forEach((type) => {
                if (renderInfo.some((e) => e.type === type)) {
                    newRenderModelTypes.push(type);
                }
            });
            setRenderModelTypes(newRenderModelTypes);

            renderModelViewer(renderInfo, true);

            // Signal icons and tooltips
            if (rightPanels?.signals?.data && rightPanels?.signals?.data.length > 0) {
                PrintDeviceIcons(rightPanels?.signals?.data, 0);

                /* ilboc 04/05/2023 */
                // PrintAlarmIcons(rightPanels?.signals?.data, 1);
            } else {
                setIsViewerLoading(false);
                window.NOP_VIEWER.deleteDeviceIcons();
            }
        } else {
            enqueueSnackbar(`${t("viewer.messages.assetNotModel")}`, {
                variant: "warning",
                anchorOrigin: {
                    horizontal: "right",
                    vertical: "bottom",
                },
            });
            setIsViewerLoading(false);
        }
        // Right panels
        if (rightPanels) {
            // setRightPanelsData(rightPanels);
            setRightPanelsData((prevState) => ({
                ...prevState,
                ...rightPanels,
            }));
            setIsRightPanelOpen(true);
        }
    };

    const fetchingAssetInformationById = (
        projectId: string,
        assetId: string,
        renderViewer = true,
        assetTag?: string | undefined
    ) => {
        setRightPanelsData(null);
        setIsViewerLoading(true);
        setRightPanelButtonsLoading(true);
        setIsRightPanelOpen(false);
        setDbidsToRender([]);
        const getAssetInformationByIdQuery: FetchAssetInformationByIdQuery = {
            projectId,
            id: assetId,
            userId,
        };

        // promesa que se resuelve siempre ofreciendo el panel de documentos (con o sin info)
        const fetchDocumentsPromise = new Promise<DocumentsRightPanelModel>((resolve, reject) => {
            if (assetTag) {
                fetchDocumentsByTagApi(project.id, assetTag)
                    .then((res) => {
                        const modifiedRes = {
                            icon: "PictureAsPdf",
                            title: "Documentos",
                            description: null,
                            data: res.data,
                        };
                        resolve(modifiedRes);
                    })
                    .catch((err) => {
                        console.error(err);
                        resolve({
                            icon: "PictureAsPdf",
                            title: "Documentos",
                            description: null,
                            data: null,
                        });
                    });
            } else {
                const modifiedRes = {
                    icon: "PictureAsPdf",
                    title: "Documentos",
                    description: null,
                    data: null,
                };
                resolve(modifiedRes);
            }
        });

        fetchAssetInformationById(getAssetInformationByIdQuery)
            .then((res) => {
                if (res.status === "success") {
                    window.NOP_VIEWER.resetModel(false);
                    window.NOP_VIEWER.dbidsToRender = [];
                    manageAssetInformation(res.data);
                    window.NOP_VIEWER.mode = ViewerMode.AssetInfo;
                    setViewerMode(window.NOP_VIEWER.mode);
                }
                return res.data;
            })
            .then((data) => {
                const rightPanelInfoPromises = [
                    fetchPropertyPanel(getAssetInformationByIdQuery).then((res) => {
                        if (res.status === "success") {
                            setRightPanelsData((prevState) => ({
                                ...prevState,
                                info: res.data,
                            }));
                        }
                    }),
                    fetchThreadsPanel(getAssetInformationByIdQuery).then((res) => {
                        if (res.status === "success") {
                            setRightPanelsData((prevState) => ({
                                ...prevState,
                                chat: res.data,
                            }));
                        }
                    }),
                    fetchSignalPanel(getAssetInformationByIdQuery).then((res) => {
                        if (res.status === "success") {
                            setRightPanelsData((prevState) => ({
                                ...prevState,
                                signals: res.data,
                            }));
                        }
                    }),
                    fetchAlarmPanel(getAssetInformationByIdQuery)
                        .then((res) => {
                            if (res.status === "success") {
                                setRightPanelsData((prevState) => ({
                                    ...prevState,
                                    alarms: res.data,
                                    // timeLine: timeLineDataModel,
                                }));
                            }
                        })
                        .catch((err) => {
                            console.error("=> Error alarms", err);
                        }),
                    fetchDocumentsPromise.then((res) => {
                        if (res) {
                            setRightPanelsData((prevState) => ({
                                ...prevState,
                                documents: res,
                            }));
                        }
                    }),
                ];
                // add isometrics panel if it is enabled
                if (data.rightPanels?.isometrics) {
                    rightPanelInfoPromises.push(
                        fetchIsoPanel(getAssetInformationByIdQuery).then((res) => {
                            if (res.status === "success") {
                                setRightPanelsData((prevState) => ({
                                    ...prevState,
                                    isometrics: res.data,
                                }));
                            }
                        })
                    );
                }

                Promise.allSettled(rightPanelInfoPromises).then(() => {
                    setRightPanelButtonsLoading(false);
                });
            })
            .catch((err) => {
                console.error(err);
                enqueueSnackbar(`${t("viewer.messages.assetNotModel")}`, {
                    variant: "warning",
                    anchorOrigin: {
                        horizontal: "right",
                        vertical: "bottom",
                    },
                });
                setIsViewerLoading(false);
            });
    };

    const fetchingAssetInformation = (
        assetId: string | undefined,
        assetDbId: number | undefined,
        assetTag: string | undefined
    ) => {
        setIsRightPanelOpen(false);
        setView("");
        if (assetId) {
            fetchingAssetInformationById(project.id, assetId, true, assetTag);
        }
        if (assetDbId && assetDbId !== -1) {
            setIsViewerLoading(true);
            const getAssetInformationByDbIdQuery: FetchAssetInformationByDbIdQuery = {
                projectId: project.id,
                input: { projectId: project.id, dbId: assetDbId, userId },
            };

            fetchAssetInformationByDbId(getAssetInformationByDbIdQuery)
                .then((res) => {
                    if (res.status === "success") {
                        window.NOP_VIEWER.resetModel(false);
                        window.NOP_VIEWER.dbidsToRender = [];
                        manageAssetInformation(res.data);
                        window.NOP_VIEWER.mode = ViewerMode.AssetInfo;
                        setViewerMode(window.NOP_VIEWER.mode);
                    }
                })
                .catch((err) => {
                    enqueueSnackbar(`${t("viewer.messages.assetNotModel")}`, {
                        variant: "error",
                        anchorOrigin: {
                            horizontal: "right",
                            vertical: "bottom",
                        },
                    });
                    // window.NOP_VIEWER.mode = ViewerMode.Default;
                    // setViewerMode(window.NOP_VIEWER.mode);
                    setIsViewerLoading(false);
                    // setIsRenderingItems(false);
                });
        }
    };

    const fetchingAssetInformationByDbId = (dbId: number | undefined, needRender = true) => {
        if (dbId) {
            setIsViewerLoading(true);
            setIsRightPanelOpen(false);
            setDbidsToRender([]);
            setView("");
            fetchAssetsForDbid(project.id, dbId)
                .then((res) => {
                    if (
                        res.status === "success" &&
                        res.data.assets &&
                        res.data.assets?.length > 0
                    ) {
                        if (
                            res.data.assets?.length === 1 &&
                            res.data.assets[0].id &&
                            res.data.assets[0].children &&
                            res.data.assets[0].children?.length <= 0
                        ) {
                            fetchingAssetInformationById(
                                project.id,
                                res.data.assets[0].id,
                                needRender,
                                res.data.assets[0].tag
                            );
                        } else if (res.data.assets[0].children!.length >= 0) {
                            setIsViewerLoading(false);
                            setAssetsWithSameDbid(res.data.assets);
                        }
                    }
                })
                .catch((err) => {
                    console.error(err);
                    enqueueSnackbar(`${t("viewer.messages.assetNotModel")}`, {
                        variant: "error",
                        anchorOrigin: {
                            horizontal: "right",
                            vertical: "bottom",
                        },
                    });
                    setIsViewerLoading(false);
                });
        }
    };

    const showEditAssetForm = (dbIdArr: number[] | undefined | never[]) => {
        if (dbIdArr && dbIdArr[0]) {
            setDbIdsToEdit(dbIdArr);
        } else {
            setDbIdsToEdit(undefined);
        }
    };

    const handleModelClick = (event: AggregateSelectionEvent) => {
        if (event.selections.length <= 0 || event.selections[0].model.myData.urn !== model.urn)
            return;
        if (window.NOP_VIEWER.mode === ViewerMode.EditAsset) {
            showEditAssetForm(event.selections[0].dbIdArray);
            window.NOP_VIEWER.setSelectionColor(
                new THREE.Color(0x9600ff),
                Autodesk.Viewing.SelectionMode.REGULAR
            );

            window.NOP_VIEWER.setCanvasClickBehavior({
                click: {
                    onObject: ["selectToggle"],
                    offObject: ["none"],
                },
            });
        } else if (
            (window.NOP_VIEWER.mode === ViewerMode.Default ||
                window.NOP_VIEWER.mode === ViewerMode.AssetInfo) &&
            window.NOP_VIEWER.coordinatesMode === false
        ) {
            fetchingAssetInformationByDbId(event.selections[0].dbIdArray[0]);

            window.NOP_VIEWER.setCanvasClickBehavior({
                click: {
                    onObject: ["selectOnly"],
                    offObject: ["deselectAll"],
                },
            });
        }
    };

    return {
        fetchingAssetInformation,
        handleModelClick,
        colorRenderModel,
        fetchingAssetInformationById,
        renderModelViewer,
        renderHTMLTooltips,
    };
};

export default useAsset;
